import React, { useState, useEffect, useCallback } from 'react';
import Header, { BreadcrumbItem } from '../components/Header';
import ContentReader from '../components/ContentReader';
import HistoryButton from '../components/HistoryButton';
import StepHelpButton from '../components/StepHelpButton';
import HistoryModal from '../components/HistoryModal';
import Step10InventoryModal from '../components/Step10InventoryModal';
import { progressService, ProgressItem, JourneyProgress } from '../services/progressService';
import { useStepWorkspaceRevision } from '../context/StepWorkspaceContext';
import { useAuthErrorHandler } from '../hooks/useAuthErrorHandler';
import { AuthErrorDisplay } from '../components/AuthErrorDisplay';
import styles from '../styles/stepCommon.module.css';
import StepActions, { ActionItem } from '../components/StepActions';
import StepLoginPrompt from '../components/StepLoginPrompt';
import StepShareDialog from '../components/StepShareDialog';
import { useStepShareDialog } from '../hooks/useStepShareDialog';
import { withShareButton } from '../utils/stepShareUtils';
import { loadStep10Inventories } from '../utils/stepShareDataLoaders';
import { mergeStepActions, stepDataWithActions, buildStepDataWithActions } from '../utils/mergeStepActions';
import { perfMark, perfSummary, perfTimeAsync } from '../utils/perfTimer';
import { getStepActionSchema } from '../config/stepActionSchemas';

interface Step10Props {
  user?: any;
  onLogin: () => void;
  onLogout: () => void;
}

const Step10: React.FC<Step10Props> = ({ user, onLogin, onLogout }) => {
  const stepWorkspaceRevision = useStepWorkspaceRevision();
  const [isContentReaderOpen, setIsContentReaderOpen] = useState(false);
  const [isGuidanceOpen, setIsGuidanceOpen] = useState(false);
  const [isInventoryModalOpen, setIsInventoryModalOpen] = useState(false);
  const [entryCount, setEntryCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [dailyInventories, setDailyInventories] = useState<{ date: string; note: string }[]>([]);
  const [collapsedActions, setCollapsedActions] = useState<Set<number>>(new Set());
  // Define the original action structure
  const originalActions: ActionItem[] = [
    {
      id: 1,
      title: "Read the SAA Green Book about Step Ten",
      description: "Discover how daily personal inventory and prompt admission sustain clarity and connection in my recovery",
      completed: false,
      hasButton: true,
      buttonText: "Read Green Book",
      buttonAction: () => setIsContentReaderOpen(true)
    },
    {
      id: 2,
      title: "Read guidance on working Step Ten",
      description: "Read practical guidance on how to work Step Ten effectively.",
      completed: false,
      hasButton: true,
      buttonText: "Read Guidance",
      buttonAction: () => setIsGuidanceOpen(true)
    },
    {
      id: 3,
      title: "Take Step Ten: Continue to take personal inventory and promptly admit when wrong",
      description: "Practice ongoing self-examination and admit mistakes promptly",
      completed: false,
      hasButton: true,
      buttonText: "Practice Inventory",
      buttonAction: () => setIsInventoryModalOpen(true)
    },
    {
      id: 4,
      title: "Share and review your work with your sponsor",
      description: "Review your Step Ten work with your sponsor. You can check this off when you've shared or discussed your work — using the share button is optional.",
      completed: false
    },
    {
      id: 5,
      title: "Take Step Ten: Continued to take personal inventory and when we were wrong promptly admitted it",
      description: "Complete Step Ten by acknowledging your inventory practice",
      completed: false
    }
  ];

  const [actions, setActions] = useState<ActionItem[]>(originalActions);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [stepData, setStepData] = useState<any>(null);
  const [isOptedOut, setIsOptedOut] = useState(false);
  const { authError, errorMessage, handleError, clearError } = useAuthErrorHandler();

  const getShareContext = useCallback(() => ({ dailyInventories }), [dailyInventories]);
  const { openShareDialog, shareDialogProps } = useStepShareDialog(10, user, actions, getShareContext);

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/', active: false },
    { label: 'SAA', path: '/saa', active: false },
    { label: '12 Steps', path: '/saa/12-steps', active: false },
    { label: 'Step Ten', path: '/saa/12-steps/step-10', active: true }
  ];

  // Check if an entry is complete (has date and note text)
  const isEntryComplete = (entry: any): boolean => {
    if (!entry || typeof entry !== 'object') return false;
    
    const date = entry.date && typeof entry.date === 'string' ? entry.date.trim() : '';
    const note = entry.note && typeof entry.note === 'string' ? entry.note.trim() : '';
    
    return !!(date.length > 0 && note.length > 0);
  };

  // Load entry count and completion status
  const loadEntryCount = async () => {
    if (!user) {
      setEntryCount(0);
      setCompletedCount(0);
      setDailyInventories([]);
      return;
    }
    
    try {
      const inventoryEntries = await loadStep10Inventories();
      setDailyInventories(inventoryEntries);
      setEntryCount(inventoryEntries.length);
      
      // Count completed entries using the same logic
      const completed = inventoryEntries.filter(item => isEntryComplete(item)).length;
      setCompletedCount(completed);
    } catch (error) {
      // If no entries exist yet, counts are 0
      setEntryCount(0);
      setCompletedCount(0);
      setDailyInventories([]);
    }
  };

  const applyLoadedStepProgress = async (
    existingProgress: ProgressItem | null,
    journeyProgress: JourneyProgress | null
  ) => {
    if (journeyProgress) {
      setIsOptedOut(journeyProgress.opted_out);
    }

    if (existingProgress) {
      const savedData = existingProgress.data || {};
      setStepData(existingProgress);

      if (savedData.actions) {
        const merged = mergeStepActions(10, originalActions, savedData, !!existingProgress.completed);
        setActions(merged.actions);
        if (merged.migrationApplied) {
          await progressService.updateStepProgress({
            program: 'SAA',
            track: '12-steps',
            step_number: 10,
            step_title: 'Step Ten',
            completed: !!existingProgress.completed,
            data: buildStepDataWithActions(merged.actions, merged.actionSchemaVersion, savedData),
          });
        }
      }
    }
  };

  const refreshStepData = async () => {
    if (!user) {
      setIsInitializing(false);
      return;
    }

    setIsInitializing(true);

    try {
      perfMark('Step10', 'refreshStepData start');
      const [{ steps, journeyProgress }] = await Promise.all([
        perfTimeAsync('Step10 loadStepPagesProgress (parallel)', () =>
          progressService.loadStepPagesProgress('SAA', '12-steps', [10], { includeJourney: true })
        ),
        loadEntryCount(),
      ]);

      await applyLoadedStepProgress(steps[10] ?? null, journeyProgress);
      clearError();
      perfSummary('Step Ten page');
    } catch (err) {
      console.error('Error loading step progress:', err);
      handleError(err, 'Failed to load your progress. Please try refreshing the page.');
    } finally {
      setIsInitializing(false);
    }
  };

  useEffect(() => {
    refreshStepData();
  }, [user, stepWorkspaceRevision]);

  const handleHistoryStepUpdated = () => {
    setIsHistoryModalOpen(false);
    if (user) {
      void refreshStepData();
    }
  };

  const getCurrentActionIndex = () => {
    return actions.findIndex(action => !action.completed);
  };

  const isActionAvailable = (actionIndex: number) => {
    if (actionIndex === 0) return true;
    return actions.slice(0, actionIndex).every(a => a.completed);
  };

  const canCompleteAction = (actionId: number) => {
    if (actionId === 4 || actionId === 5) {
      const action3 = actions.find(a => a.id === 3);
      return action3 ? action3.completed : false;
    }
    return true;
  };

  const handleActionChange = async (updatedActions: ActionItem[]) => {
    if (!user) {
      handleError(new Error('User not logged in'), 'Please log in to save your progress.');
      return;
    }
    
    try {
      // Update local state
      setActions(updatedActions);
      
      // Save to backend
      const allCompleted = updatedActions.every(action => action.completed);
      const updatedStepData = await progressService.updateStepProgress({
        program: 'SAA',
        track: '12-steps',
        step_number: 10,
        step_title: 'Step Ten',
        completed: allCompleted,
        data: stepDataWithActions(updatedActions, getStepActionSchema(10).actionSchemaVersion)
      });
      
      // Update step data for history modal
      if (updatedStepData && updatedStepData.step) {
        setStepData(updatedStepData.step);
      }
      
      // Update status note
      const completedCount = updatedActions.filter(a => a.completed).length;
      await progressService.updateStatus({
        program: 'SAA',
        track: '12-steps',
        status_note: `Working Step Ten - ${completedCount} of 5 actions completed`,
        progress_milestone: allCompleted ? 'step10_completed' : 'step10_in_progress'
      });
      
      clearError();
    } catch (err: any) {
      console.error('Error updating action progress:', err);
      handleError(err, 'Failed to save progress. Please try again.');
    }
  };

  // Check if all actions are completed
  const allCompleted = actions.every(action => action.completed);

  return (
    <div className={styles.stepPage}>
      <Header 
        breadcrumbs={breadcrumbs}
        user={user}
        onLogout={onLogout}
        onLogin={onLogin}
      />
      
      <main className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.stepTitleContainer}>
            <h1 className={styles.stepTitle}>Step Ten</h1>
            <StepHelpButton stepKey="app" />
            {stepData && (
              <HistoryButton
                stepNumber={10}
                stepTitle="Step Ten"
                stepData={stepData}
                isOptedOut={isOptedOut}
                onStepUpdated={() => setIsHistoryModalOpen(true)}
              />
            )}
          </div>
          <p className={styles.stepSubtitle}>
            Continued to take personal inventory and when we were wrong promptly admitted it.
          </p>
        </div>

        {errorMessage && (
          <div className={styles.errorMessage}>
            {authError ? (
              <AuthErrorDisplay
                error={errorMessage}
                onLogin={onLogin}
                onRefresh={() => window.location.reload()}
              />
            ) : (
              <>
                <p>{errorMessage}</p>
                <button onClick={() => window.location.reload()}>Refresh Page</button>
              </>
            )}
          </div>
        )}

        {!user && !isInitializing && !errorMessage && (
          <StepLoginPrompt onLogin={onLogin} />
        )}

        {user && isInitializing && !errorMessage && (
          <div className={styles.loadingMessage}>
            <p>Loading your progress...</p>
          </div>
        )}

        {user && !isInitializing && !errorMessage && (
          <div className={styles.actionsContainer}>
            <h2 className={styles.actionsTitle}>Actions to Complete Step Ten</h2>
            <p className={styles.actionsDescription}>
              Complete each action in order. Check the box when you've finished each action.
            </p>
            
            <StepActions
              actions={withShareButton(10, actions, openShareDialog)}
              onActionChange={handleActionChange}
              canCompleteAction={canCompleteAction}
              stepNumber={10}
              renderActionContent={(action) => {
                if (action.id === 3) {
                  return (
                    <div
                      className={styles.actionContentIndent}
                      style={{
                        marginTop: '0.5rem',
                        color: '#6b7280',
                        fontSize: '0.875rem',
                      }}
                    >
                      {entryCount === 0 ? (
                        <p style={{ margin: 0, fontStyle: 'italic' }}>
                          Practice taking a nightly inventory when you're ready. Click "Practice Inventory" to add entries — or check off this action anytime; there's no minimum number of entries.
                        </p>
                      ) : (
                        <span>
                          {completedCount} {completedCount === 1 ? 'entry complete' : 'entries complete'}
                          {entryCount > completedCount &&
                            ` (${entryCount - completedCount} incomplete)`}
                          .
                        </span>
                      )}
                    </div>
                  );
                }
                return null;
              }}
            />

            {allCompleted && (
              <>
                <div className={styles.completionMessage}>
                  <h3>🎉 Congratulations!</h3>
                  <p>You have completed all actions for Step Ten and can move on to Step Eleven.</p>
                </div>
                <div className={styles.stepNavigation}>
                  <div className={styles.navigationLinks}>
                    <a href="/saa/12-steps/step-9" className={styles.prevStepLink}>
                      {'<<'} Step Nine
                    </a>
                    <span></span>
                    <a href="/saa/12-steps/step-11" className={styles.nextStepLink}>
                      Step Eleven {'>>'}
                    </a>
                  </div>
                </div>
              </>
            )}

            {!allCompleted && (
              <div className={styles.stepNavigation}>
                <div className={styles.navigationLinks}>
                  <a href="/saa/12-steps/step-9" className={styles.prevStepLink}>
                    {'<<'} Step Nine
                  </a>
                  <span></span>
                  <a href="/saa/12-steps/step-11" className={styles.nextStepLink}>
                    Step Eleven {'>>'}
                  </a>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

      {/* ContentReader for Green Book */}
      <ContentReader
        isOpen={isContentReaderOpen}
        onClose={() => setIsContentReaderOpen(false)}
        filePath="/content/010_CHAPTER_THREE.md"
        startTag="<<step-ten start>>"
        endTag="<<step-ten end>>"
      />

      {/* ContentReader for How to Work Step Ten */}
      <ContentReader
        isOpen={isGuidanceOpen}
        onClose={() => setIsGuidanceOpen(false)}
        filePath="/content/Step_10_Guidance.md"
        title="Guidance on working Step Ten"
        startTag="<<working-step-ten start>>"
        endTag="<<working-step-ten end>>"
      />

      {/* Step 10 Inventory Modal */}
      <Step10InventoryModal
        isOpen={isInventoryModalOpen}
        onClose={() => setIsInventoryModalOpen(false)}
        user={user}
        onListUpdate={loadEntryCount}
      />

      {/* History Modal */}
      <HistoryModal
        isOpen={isHistoryModalOpen}
        onClose={() => setIsHistoryModalOpen(false)}
        stepNumber={10}
        stepTitle="Step Ten"
        stepData={stepData}
        onStepUpdated={handleHistoryStepUpdated}
      />

      <StepShareDialog {...shareDialogProps} />
    </div>
  );
};

export default Step10; 